import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { useEffect, useState } from 'react'
import { TextButton } from '@shipt/design-system-buttons'
import { Stack } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useScrollWithThreshold } from '@/features/shared/components/Headers/useScrollWithThreshold'
import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import {
  getIsC360ElementExpired,
  setSeenC360Element,
} from '@/features/entrypoint/components/Circle360/utils'
import { useStoreId } from '@/features/account/services/User/hooks'
import { useIsEligibleForC360NoMarkup } from '@/features/entrypoint/components/Circle360/useIsEligibleForC360NoMarkup'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { DynamicStoreInfoDrawer } from '@/features/shop/components/Drawer/StoreInfoDrawer/DynamicStoreInfoDrawer'
import { C360BannerWithCloseButtonContainer } from '@/features/entrypoint/components/Circle360/C360TotalSavingsBanner'

export const Circle360NMUBanner = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const { screenSm } = useMediaQuery()
  const [closeButtonClicked, setCloseButtonClicked] = useState(false)
  const { openDialog } = useHandleDialog()
  const storeId = useStoreId()
  const hasExpired = getIsC360ElementExpired('banner', storeId)
  const isEligibleForC360NoMarkup = useIsEligibleForC360NoMarkup()
  const showC360Banner = hasExpired && isEligibleForC360NoMarkup

  const isPastScrollThreshold = useScrollWithThreshold(90, {
    enabled: screenSm && showC360Banner,
  })

  useEffect(() => {
    if (showC360Banner) {
      trackElementViewed({
        shelf_index: 0,
        shelf_name: 'banner',
        content: 'exclusive_savings',
        type: 'banner',
        link_name: 'c360_top_banner',
      })
    }
  }, [showC360Banner])

  const handleCloseBanner = () => {
    setCloseButtonClicked(true)
    setSeenC360Element('banner', storeId)
    trackElementClicked({
      shelf_index: 0,
      shelf_name: 'banner',
      content: 'exclusive_savings',
      type: 'button',
      link_name: 'dismiss',
    })
  }

  const handleStorePricingClick = () => {
    openDialog(DynamicStoreInfoDrawer, {
      defaultTab: 'storeInfo',
    })
  }

  if (closeButtonClicked || !showC360Banner) return null

  return (
    <CMSWrapper
      {...getCMSWrapperProps({ content_type_id, id })}
      isPastScrollThreshold={isPastScrollThreshold}
    >
      <C360BannerWithCloseButtonContainer onClick={handleCloseBanner}>
        <Stack
          align={{ base: 'start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          spacing={{ md: 'lg' }}
        >
          <Body
            strong
            size={{ base: 'md', md: 'lg', lg: 'xl' }}
            lineHeight="compact"
            id="exclusive-savings"
          >
            Enjoy member-exclusive savings
          </Body>
          <SecondaryCopy size={{ base: 'sm', md: 'lg', lg: 'xl' }} lineHeight="compact">
            You're paying $0 in markups.{' '}
            <TermsButton
              concept="accent"
              isLink
              onClick={handleStorePricingClick}
              aria-describedby="exclusive-savings"
            >
              Terms apply
            </TermsButton>
          </SecondaryCopy>
        </Stack>
      </C360BannerWithCloseButtonContainer>
    </CMSWrapper>
  )
}

const CMSWrapper = styled(GridLayoutContainer)<{
  isPastScrollThreshold: boolean
}>`
  position: sticky;
  z-index: 4;

  @media ${screenSizes.max_mobile} {
    transition: top 0.6s ease;
    top: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? '72px' : '134px')};
  }

  @media ${screenSizes.tablet} {
    top: 120px;
    margin-bottom: 2.5rem;
  }
`

const SecondaryCopy = styled(Body)`
  @media ${screenSizes.tablet} {
    display: flex;
    gap: 1rem;
  }
`

const TermsButton = styled(TextButton)`
  font-size: inherit;
  font-weight: inherit;
  text-decoration: underline;
  text-underline-offset: 0.125rem;
`
